import { useState } from 'react'
import { motion, Reorder } from 'framer-motion'
import { Plus, Edit2, Trash2, Eye, EyeOff, GripVertical, Users, Mail } from 'lucide-react'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { team as defaultTeam } from '../../data/seedData'
import { Button, Modal, Input, Textarea, Card, Badge, Checkbox } from '../../components/common'

const TeamMemberForm = ({ member, onSave, onClose }) => {
  const [formData, setFormData] = useState(member || {
    name: '',
    role: '',
    bio: '',
    imageUrl: '',
    email: '',
    phone: '',
    order: 0,
    isPublished: true,
  })
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useFileUpload()

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await upload(file, `team/${Date.now()}_${file.name}`)
    if (result.success) {
      setFormData({ ...formData, imageUrl: result.url })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Full Name"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Pastor John Adebayo"
        />
        <Input
          label="Role/Title"
          required
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          placeholder="Senior Pastor"
        />
      </div>

      <Textarea
        label="Bio"
        rows={4}
        value={formData.bio}
        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
        placeholder="Brief biography..."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Email (optional)"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="pastor@dlbcclarksville.org"
        />
        <Input
          label="Phone (optional)"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="(931) 555-0123"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Profile Photo
        </label>
        <div className="flex items-center gap-4">
          {formData.imageUrl ? (
            <img
              src={formData.imageUrl}
              alt="Preview"
              className="w-20 h-20 rounded-full object-cover"
            />
          ) : (
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <span className="text-sm">{uploading ? 'Uploading...' : 'Upload Photo'}</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <Checkbox
        label="Published (visible on website)"
        checked={formData.isPublished}
        onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {member ? 'Update Member' : 'Add Member'}
        </Button>
      </div>
    </form>
  )
}

const LeadershipManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: team, add, update, remove } = useCollection('team', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const displayTeam = team?.length > 0 ? team : defaultTeam

  const handleSave = async (data) => {
    if (editingMember?.id) {
      await update(editingMember.id, data)
    } else {
      await add({ ...data, order: displayTeam.length })
    }
    setShowModal(false)
    setEditingMember(null)
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await remove(deleteConfirm)
      setDeleteConfirm(null)
    }
  }

  const handleReorder = async (newOrder) => {
    for (let i = 0; i < newOrder.length; i++) {
      if (newOrder[i].order !== i) {
        await update(newOrder[i].id, { order: i })
      }
    }
  }

  const togglePublished = async (member) => {
    await update(member.id, { isPublished: !member.isPublished })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Leadership Team</h1>
          <p className="text-gray-500">Manage pastors and ministry leaders</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingMember(null)
            setShowModal(true)
          }}
        >
          Add Member
        </Button>
      </div>

      {/* Team List */}
      <Card animate={false} className="p-6">
        <p className="text-sm text-gray-500 mb-4">
          Drag and drop to reorder team members.
        </p>

        <Reorder.Group
          axis="y"
          values={displayTeam}
          onReorder={handleReorder}
          className="space-y-3"
        >
          {displayTeam.map((member) => (
            <Reorder.Item
              key={member.id}
              value={member}
              className="bg-gray-50 rounded-xl p-4 cursor-move"
            >
              <div className="flex items-center gap-4">
                <GripVertical className="w-5 h-5 text-gray-400" />

                {member.imageUrl ? (
                  <img
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-gray-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-dark-100">{member.name}</h3>
                  <p className="text-sm text-gray-500">{member.role}</p>
                  {member.email && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" />
                      {member.email}
                    </p>
                  )}
                </div>

                <Badge
                  variant={member.isPublished ? 'success' : 'gray'}
                  size="sm"
                >
                  {member.isPublished ? 'Published' : 'Hidden'}
                </Badge>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => togglePublished(member)}
                    className={`p-2 rounded-lg transition-colors ${
                      member.isPublished
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {member.isPublished ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingMember(member)
                      setShowModal(true)
                    }}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(member.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>

        {displayTeam.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No team members yet</p>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingMember(null)
        }}
        title={editingMember ? 'Edit Team Member' : 'Add Team Member'}
        size="lg"
      >
        <TeamMemberForm
          member={editingMember}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingMember(null)
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Team Member"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this team member? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default LeadershipManager
